import React from 'react';
import {
  Box,
  ButtonBase,
  CircularProgress,
  Dialog,
  Drawer,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import analytics from '../../../../utils/analytics';
import { getCompletedComplements, postComplementInvite } from '../../../../queries/complementInvite';
import type {
  CompletedComplement,
  DeclaredComplementRelationship,
} from '../../../../types/complementInvite';
import ShareDialog from './ShareDialog';
import {
  LEAD_PICKER_PAGE,
  LEAD_SHARE_PAGE,
  leadResultCategory,
} from './analyticsPages';
import {
  COMPLEMENT_DISCLOSURE,
  mintErrorCopy,
  OTHER_COMPLETED_TITLE,
  PICKER_NOTICE,
  PICKER_TITLE,
  RELATIONSHIP_LABELS,
} from './copy';
import { plr } from '../landing/tokens';
import { chevronRightIcon } from '../landing/icons';

type CandidateDialogProps = {
  open: boolean;
  token: string;
  onClose: () => void;
  pageCategory?: string;
};

const RELATIONSHIPS: DeclaredComplementRelationship[] = [
  'conyuge',
  'hijos_en_comun',
  'hijo',
  'padre',
  'hermano',
  'otro',
];

const optionSx = {
  width: '100%',
  minHeight: 52,
  boxSizing: 'border-box',
  border: `1px solid ${plr.border}`,
  borderRadius: '16px',
  background: '#FFFFFF',
  color: plr.ink,
  fontFamily: plr.font,
  fontSize: 15.5,
  fontWeight: 600,
  textAlign: 'left',
  p: '0 18px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '12px',
  transition: 'border-color .15s',
  '&:hover': { borderColor: plr.inkHover },
  '&.Mui-focusVisible': { outline: `3px solid ${plr.stepCurrent}`, outlineOffset: '2px' },
  '&.Mui-disabled': { opacity: 0.6 },
} as const;

// Selector de vínculo (4f/4fb): hoja inferior en mobile, diálogo centrado en desktop.
// Tocar una opción genera el enlace directamente (antes: radio + "Continuar").
export default function CandidateDialog({
  open, token, onClose, pageCategory = leadResultCategory(),
}: CandidateDialogProps) {
  const theme = useTheme();
  const desktop = useMediaQuery(theme.breakpoints.up('sm'));
  const [completed, setCompleted] = React.useState<CompletedComplement[]>([]);
  const [submitting, setSubmitting] = React.useState<DeclaredComplementRelationship | null>(null);
  const [invite, setInvite] = React.useState<{ url: string, expiresAt: string } | null>(null);
  const [mintError, setMintError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!open) return;
    analytics.page(pageCategory, LEAD_PICKER_PAGE);
    setCompleted([]);
    setMintError(null);
    getCompletedComplements(token)
      .then(({ data }) => setCompleted(data.completedComplements || []))
      .catch(() => setCompleted([]));
  }, [open, token]);

  const handleClose = () => {
    if (submitting) return;
    analytics.track('Cerrar Dialog', { initiator: 'usuario' });
    onClose();
  };

  const submit = async (relationship: DeclaredComplementRelationship) => {
    setSubmitting(relationship);
    setMintError(null);
    analytics.track('Click Boton', {
      text: RELATIONSHIP_LABELS[relationship],
      initiator: 'usuario',
    });
    try {
      const { data } = await postComplementInvite({
        token,
        declaredRelationship: relationship,
      });
      setInvite({ url: data.url, expiresAt: data.expiresAt });
    } catch (e) {
      setMintError(mintErrorCopy(e));
    } finally {
      setSubmitting(null);
    }
  };

  if (invite) {
    return (
      <ShareDialog
        open
        url={invite.url}
        expiresAt={invite.expiresAt}
        pageCall={{ category: pageCategory, name: LEAD_SHARE_PAGE }}
        onClose={() => { setInvite(null); onClose(); }}
      />
    );
  }

  const content = (
    <Box
      data-pw="complement-picker"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        fontFamily: plr.font,
        color: plr.ink,
        textAlign: 'left',
      }}
    >
      {!desktop && (
        <Box
          component="span"
          aria-hidden="true"
          sx={{
            alignSelf: 'center', width: 40, height: 4, borderRadius: '2px', background: plr.heroText,
          }}
        />
      )}
      <Box
        component="h2"
        id="complement-picker-title"
        sx={{ m: 0, fontSize: desktop ? 21 : 19, fontWeight: 800 }}
      >
        {PICKER_TITLE}
      </Box>
      <Box
        component="p"
        sx={{
          m: '-4px 0 2px', fontSize: desktop ? 14 : 13.5, lineHeight: 1.45, color: plr.body,
        }}
      >
        {PICKER_NOTICE}
      </Box>
      {RELATIONSHIPS.map((relationship) => (
        <ButtonBase
          key={relationship}
          data-pw={`complement-relationship-${relationship}`}
          onClick={() => submit(relationship)}
          disabled={Boolean(submitting)}
          sx={optionSx}
        >
          {RELATIONSHIP_LABELS[relationship]}
          {submitting === relationship
            ? <CircularProgress size={16} sx={{ color: plr.muted }} />
            : chevronRightIcon({ size: 16, color: plr.muted })}
        </ButtonBase>
      ))}
      {completed.length > 0 && (
        <Box sx={{ mt: '4px' }}>
          <Box
            component="span"
            sx={{
              display: 'block', fontSize: 12, fontWeight: 700, color: plr.muted, mb: '8px',
            }}
          >
            {OTHER_COMPLETED_TITLE}
          </Box>
          {completed.map((person) => (
            <Box
              key={`${person.relationship}-${person.displayName}`}
              sx={{
                mb: '8px',
                p: '10px 14px',
                borderRadius: '14px',
                background: plr.surface,
                display: 'flex',
                flexDirection: 'column',
                gap: '2px',
              }}
            >
              <Box component="b" sx={{ fontSize: 14 }}>{person.displayName}</Box>
              <Box component="span" sx={{ fontSize: 12.5, color: plr.body }}>
                {RELATIONSHIP_LABELS[person.relationship]}
              </Box>
            </Box>
          ))}
        </Box>
      )}
      {mintError && (
        <Box component="p" role="alert" sx={{ m: 0, fontSize: 13, color: theme.palette.error.main }}>
          {mintError}
        </Box>
      )}
      <Box
        component="p"
        sx={{
          m: '2px 0 0', fontSize: desktop ? 12 : 11.5, lineHeight: 1.45, color: plr.muted,
        }}
      >
        {COMPLEMENT_DISCLOSURE}
      </Box>
    </Box>
  );

  const backdropSx = { '& .MuiBackdrop-root': { background: plr.overlay } };

  if (desktop) {
    return (
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="complement-picker-title"
        sx={backdropSx}
        PaperProps={{
          sx: {
            width: 440,
            maxWidth: 'calc(100% - 32px)',
            borderRadius: '24px',
            p: '28px',
            boxSizing: 'border-box',
            boxShadow: '0 16px 40px rgba(26,20,61,0.25)',
          },
        }}
      >
        {content}
      </Dialog>
    );
  }

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={handleClose}
      sx={backdropSx}
      PaperProps={{
        role: 'dialog',
        'aria-modal': true,
        'aria-labelledby': 'complement-picker-title',
        sx: {
          borderRadius: '24px 24px 0 0',
          p: '12px 24px 24px',
          boxShadow: '0 -8px 24px rgba(26,20,61,0.18)',
          maxHeight: '90vh',
        },
      } as object}
    >
      {content}
    </Drawer>
  );
}

CandidateDialog.defaultProps = {
  pageCategory: leadResultCategory(),
};
